import React, { useState, useEffect } from 'react';
import { basePort, headers, getJwtFromCookies, isExpiredJwt, IsMbrRoleCP, IsMbrRolePv, IsMbrRoleAdmin, setTokenHeader } from "../../module_auth/AuthService";
import parse from 'html-react-parser';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { formatDate, sendGoogleAnalytics } from "../../component/common/BaseUtil";
import { ModalMessage } from "../../component/common/Modal";
import { ServiceDisclaimerCard } from "../../component/common/ServiceDisclaimerCard";
import '../../App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faUser } from '@fortawesome/free-regular-svg-icons';
import { faLaptop, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { TALENT_IMAGE_URL_PREFIX } from "../../Config";

export function TalentService() {
    const location = useLocation();
    const navigate = useNavigate();
    const { regId } = useParams();

    // 상태 관리 (재능 서비스 전용)
    const [title, setTitle] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [priceValue, setPriceValue] = useState(0);
    const [priceUnit, setPriceUnit] = useState("");
    const [isPriceNegotiable, setIsPriceNegotiable] = useState(0);
    const [priceNote, setPriceNote] = useState("");
    const [descriptionText, setDescriptionText] = useState("");
    const [expertIntroText, setExpertIntroText] = useState("");
    const [hits, setHits] = useState(0);
    const [nickname, setNickname] = useState("");
    const [selectedCityId, setSelectedCityId] = useState("");
    const [isRemote, setIsRemote] = useState(false);
    const [cityName, setCityName] = useState("");

    // 이미지/파일 관련
    const [mainImageUrl, setMainImageUrl] = useState(""); // 메인 이미지 (단일 문자열)
    const [detailImageUrls, setDetailImageUrls] = useState([]); // 디테일 이미지 (배열, 최대 4개)
    const [updateAt, setUpdateAt] = useState("");

    const [showModal, setShowModal] = useState(false);
    const [inquiryData, setInquiryData] = useState({ content: '', contact: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [showAuthGuideModal, setShowAuthGuideModal] = useState(false);
    const [authGuideTitle, setAuthGuideTitle] = useState("");
    const [authGuideMessage, setAuthGuideMessage] = useState("");
    const [authGuideTargetUrl, setAuthGuideTargetUrl] = useState("/");

    useEffect(() => {
        sendGoogleAnalytics(location.pathname);
        getTalentService();
    }, [regId]);

    function getTalentService() {
        let url = basePort + '/get/talent/' + regId; // 재능 상세 API 호출
        setIsLoading(true);
        fetch(url, { method: 'GET', headers })
            .then(async response => {
                const responseBody = await response.json().catch(() => null);

                if (response.status === 404) {
                    throw new Error(responseBody?.message || responseBody?.detail || "해당 서비스를 찾을 수 없습니다.");
                }

                if (!response.ok) {
                    throw new Error(responseBody?.message || responseBody?.detail || "서버 통신 중 오류가 발생했습니다.");
                }

                return responseBody;
            })
            .then(res => {
                console.log("서버 응답 데이터:", res);
                console.log("서버 응답 데이터 이미지:", res.serviceImages);
                // --- 이미지 처리 로직 추가 ---
                if (res.serviceImages && Array.isArray(res.serviceImages)) {
                    const images = res.serviceImages;

                    // 1. 메인 이미지 찾기 (MAIN 타입 중 첫 번째)
                    const mainImgObj = images.find(img => img.imageType === "MAIN");
                    if (mainImgObj) {
                        setMainImageUrl(`${mainImgObj.fileDirectory}/${mainImgObj.uniqFileName}`);
                        console.log(`${mainImgObj.fileDirectory}/${mainImgObj.uniqFileName}`)
                    }

                    // 2. 디테일 이미지 처리 (DETAIL 타입만 필터링 -> sortOrder 정렬 -> 상위 4개)
                    const details = images
                        .filter(img => img.imageType === "DETAIL")
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .slice(0, 4)
                        .map(img => `${img.fileDirectory}/${img.uniqFileName}`);

                    setDetailImageUrls(details);
                }

                setTitle(res.title);
                setNickname(res.nickname || "정보 없음");
                setCategoryName(res.categoryName); // 백엔드에서 코드명 대신 한글명을 보내준다고 가정
                setPriceValue(res.priceValue);
                setPriceUnit(res.priceUnit);
                setIsPriceNegotiable(Number(res.isPriceNegotiable) === 1 ? 1 : 0);
                setPriceNote(res.priceNote);
                setDescriptionText(res.descriptionText || "");
                setExpertIntroText(res.expertIntroText || "");
                console.log(res.isRemote)
                setIsRemote(res.isRemote === 1);
                setSelectedCityId(res.selectedCityId)
                setCityName(res.cityName)
                setUpdateAt(res.updateAt);
                setHits(res.hits)
            })
            .catch(error => {
                console.log(error);
                alert(error.message || "해당 서비스를 찾을 수 없습니다.");
                navigate('/talent_list');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    // 가격 단위 한글 변환 helper
    const renderPriceUnit = (unit) => {
        const units = { "HOUR": "시간당", "ONCE": "회당", "DAY": "일당" };
        return units[unit] || unit;
    };

    const priceUnitLabel = renderPriceUnit(priceUnit);
    const numericPriceHeadingStyle = { fontSize: '1.5rem', lineHeight: '1.2' };
    const negotiablePriceHeadingStyle = { fontSize: '1.38rem', lineHeight: '1.2', letterSpacing: '-0.02em' };

    const hasOfflineMode = Boolean(cityName);
    const hasRemoteMode = isRemote === 1 || isRemote === true;

    const closeAuthGuideModal = () => {
        setShowAuthGuideModal(false);
    };

    const submitAuthGuideModal = () => {
        setShowAuthGuideModal(false);

        if (authGuideTargetUrl && authGuideTargetUrl !== location.pathname) {
            navigate(authGuideTargetUrl);
        }
    };

    const clickInquiryBtn = () => {
        const token = getJwtFromCookies();

        if (!token || isExpiredJwt(token)) {
            setAuthGuideTitle("로그인이 필요합니다.");
            setAuthGuideMessage("문의 기능은 로그인 후 이용할 수 있습니다.\n로그인 페이지로 이동하시겠습니까?");
            setAuthGuideTargetUrl('/login');
            setShowAuthGuideModal(true);
            return;
        }

        if (IsMbrRolePv(token)) {
            setShowModal(true);
            return;
        }

        if (IsMbrRoleCP(token) || IsMbrRoleAdmin(token)) {
            setAuthGuideTitle("개인회원 전용 기능입니다.");
            setAuthGuideMessage("전문가 문의는 개인회원만 이용할 수 있습니다.");
            setAuthGuideTargetUrl(location.pathname);
            setShowAuthGuideModal(true);
            return;
        }

        setAuthGuideTitle("회원가입이 필요합니다.");
        setAuthGuideMessage("문의 기능은 회원가입 후 이용할 수 있습니다. 회원가입 페이지로 이동하시겠습니까?");
        setAuthGuideTargetUrl('/mbr_select');
        setShowAuthGuideModal(true);
    };

    const sendInquiry = () => {
        const token = getJwtFromCookies();

        if (!token || isExpiredJwt(token)) {
            alert("로그인 세션이 만료되었습니다. 다시 로그인 후 시도해주세요.");
            setShowModal(false);
            navigate('/login');
            return;
        }

        if (!IsMbrRolePv(token)) {
            alert("전문가 문의는 개인회원만 이용할 수 있습니다.");
            setShowModal(false);
            return;
        }

        // 1. 유효성 검사
        if (!inquiryData.content.trim()) {
            alert("문의 내용을 입력해주세요.");
            return;
        }
        if (!inquiryData.contact.trim()) {
            alert("답변을 받으실 연락처를 입력해주세요.");
            return;
        }

        // 개인정보 동의 체크 여부 확인 (id="agreeCheck" 가정)
        const isAgreed = document.getElementById('agreeCheck')?.checked;
        if (!isAgreed) {
            alert("개인정보 제공 동의에 체크해주세요.");
            return;
        }

        // 2. 전송 데이터 구성
        const payload = {
            regId: regId,             // useParams()에서 가져온 서비스 ID
            content: inquiryData.content,
            senderContact: inquiryData.contact,
            // 필요 시 현재 로그인한 사용자의 ID 추가
            // senderId: currentUserId 
        };

        setTokenHeader(token);

        const requestHeaders = {
            ...headers,
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        // 3. API 호출
        fetch(basePort + '/inquiry/talent', {
            method: 'POST',
            headers: requestHeaders,
            body: JSON.stringify(payload)
        })
            .then(async (response) => {
                if (response.ok) {
                    alert("전문가에게 문의가 성공적으로 전달되었습니다.");
                    setShowModal(false); // 모달 닫기
                    setInquiryData({ content: '', contact: '' }); // 데이터 초기화
                } else if (response.status === 401) {
                    alert("로그인 인증이 만료되었거나 권한이 없습니다. 다시 로그인 후 시도해주세요.");
                    setShowModal(false);
                    navigate('/login');
                } else {
                    const errData = await response.json();
                    alert("전송 실패: " + (errData.message || "알 수 없는 오류"));
                }
            })
            .catch((error) => {
                console.error("Inquiry Error:", error);
                alert("서버 통신 중 오류가 발생했습니다.");
            });
    };

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', position: 'relative' }}>
            <main className="page landing-page" style={{ opacity: isLoading ? 0.45 : 1, pointerEvents: isLoading ? 'none' : 'auto' }}>
                <section className="container responsive-container-width" style={{ paddingTop: '100px', paddingBottom: '50px' }}>

                    {/* 브레드크럼 */}
                    <ol className="breadcrumb mb-4">
                        <li className="breadcrumb-item"><span>한인 전문가</span></li>
                        <li className="breadcrumb-item"><span>서비스 상세</span></li>
                    </ol>

                    {/* 카테고리 & 조회수 행 (공간 절약형 배치 예시) */}
                    <div className="d-flex justify-content-between align-items-end mb-2 px-1">
                        {/* 조회수를 타이틀 카드 위쪽 우측에 작게 배치해도 깔끔합니다 */}
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                            <FontAwesomeIcon icon={faUser} className="me-1" /> {/* 또는 적절한 아이콘 */}
                            {hits?.toLocaleString() || 0}
                        </div>
                    </div>

                    {/* 상단 통합 섹션 (크몽 스타일: 이미지 + 제목) */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '15px', overflow: 'hidden' }}>

                        <div className="row g-0 align-items-center">
                            {/* 1. 타이틀 & 닉네임 섹션 (왼쪽) */}
                            <div className={mainImageUrl ? "col-md-7" : "col-md-12"} style={{ position: 'relative' }}>
                                <div className="card-body p-4 p-lg-5 d-flex flex-column h-100">
                                    {/* 왼쪽 최상단 카테고리 */}
                                    <div className="mb-4">
                                        <span className="text-secondary fw-semibold" style={{ fontSize: '0.85rem' }}>
                                            {categoryName}
                                        </span>
                                    </div>

                                    {/* 메인 타이틀 (기존 위치 유지) */}
                                    <h2 className="fw-bold mb-3" style={{ fontFamily: 'Nanum Gothic', lineHeight: '1.4', wordBreak: 'keep-all' }}>
                                        {title}
                                    </h2>

                                    {/* 닉네임 */}
                                    <div className="d-flex align-items-center text-muted">
                                        <span className="fw-medium" style={{ fontSize: '1.05rem' }}>
                                            {nickname || "전문가"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* 2. 메인 이미지 섹션 (오른쪽) */}
                            {mainImageUrl && (
                                <div className="col-md-5 d-flex justify-content-center align-items-center p-3">
                                    <div style={{
                                        width: '100%',
                                        maxWidth: '320px',
                                        height: '220px',
                                        overflow: 'hidden',
                                        borderRadius: '12px'
                                    }}>
                                        <img
                                            src={TALENT_IMAGE_URL_PREFIX + mainImageUrl}
                                            alt="Service"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="row">
                        {/* 왼쪽: 서비스 상세 내용 */}
                        <div className="col-lg-8">
                            <h5 className="fw-bold mb-3 text-dark tracking-tight">서비스 설명</h5>
                            <div className="card shadow-sm border-0 mb-5" style={{ borderRadius: '15px' }}>
                                <div className="card-body ql-editor p-4">
                                    {descriptionText ? parse(descriptionText) : "설명 내용이 없습니다."}

                                    {/* 디테일 이미지 (각 이미지를 한 줄씩 크게 표시) */}
                                    {detailImageUrls.length > 0 && (
                                        <div className="mt-4 d-flex flex-column gap-3 gap-md-4">
                                            {detailImageUrls.map((url, idx) => (
                                                <div
                                                    key={idx}
                                                    className="w-100"
                                                    style={{ borderRadius: '10px', overflow: 'hidden' }}
                                                >
                                                    <img
                                                        src={TALENT_IMAGE_URL_PREFIX + url}
                                                        alt={`Detail ${idx + 1}`}
                                                        className="d-block w-100"
                                                        style={{
                                                            maxWidth: '100%',
                                                            height: 'auto',
                                                            maxHeight: '560px',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <h5 className="fw-bold mb-3 text-dark tracking-tight">전문가 소개</h5>
                            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '15px' }}>
                                <div className="card-body ql-editor p-4">
                                    {expertIntroText ? parse(expertIntroText) : "등록된 전문가 소개가 없습니다."}
                                </div>
                            </div>
                            <ServiceDisclaimerCard />
                        </div>

                        {/* 오른쪽: 가격 및 연락처 (Sticky) */}
                        <div className="col-lg-4">
                            {/* top: 100px는 네비게이션 바 높이에 따라 조절하세요. 보통 80~100px가 적당합니다. */}
                            <div className="sticky-top" style={{ top: '100px', zIndex: '10', marginBottom: '2rem', paddingTop: '40px' }}>
                                <div className="card shadow-sm border-0" style={{ borderRadius: '15px', borderTop: '5px solid #0d6efd' }}>
                                    <div className="card-body p-4">
                                        <h5 className="fw-bold text-secondary mb-3" style={{ fontSize: '1rem' }}>서비스 금액</h5>
                                        <div className="d-flex align-items-baseline mb-2">
                                            {isLoading ? (
                                                <span style={{ minHeight: '1.8rem', display: 'inline-block' }}></span>
                                            ) : isPriceNegotiable === 1 ? (
                                                <>
                                                    <h2 className="fw-bold text-dark mb-0" style={negotiablePriceHeadingStyle}>가격협의</h2>
                                                    <span className="ms-2 text-muted">/ {priceUnitLabel}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <h2 className="fw-bold text-dark mb-0" style={numericPriceHeadingStyle}>€{priceValue.toLocaleString()}</h2>
                                                    <span className="ms-2 text-muted">/ {priceUnitLabel}</span>
                                                </>
                                            )}
                                        </div>

                                        {/* 추가된 위치: 도시 및 원격 정보 */}
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            {hasOfflineMode && (
                                                <span className="badge d-flex align-items-center px-3 py-2"
                                                    style={{
                                                        backgroundColor: '#e7f1ff',
                                                        color: '#0056b3',
                                                        border: '1px solid #b3d7ff',
                                                        fontSize: '0.9rem', // 사이즈 업
                                                        borderRadius: '8px'
                                                    }}>
                                                    <FontAwesomeIcon icon={faLocationDot} className="me-2" style={{ fontSize: '1rem' }} />
                                                    <span className="fw-bold">{hasRemoteMode ? `${cityName} 대면` : cityName}</span>
                                                </span>
                                            )}

                                            {/* 원격 정보: 조금 더 선명한 초록색 톤 */}
                                            {hasRemoteMode && (
                                                <span className="badge d-flex align-items-center px-3 py-2"
                                                    style={{
                                                        backgroundColor: '#e6f4ea',
                                                        color: '#137333',
                                                        border: '1px solid #ceead6',
                                                        fontSize: '0.9rem', // 사이즈 업
                                                        borderRadius: '8px'
                                                    }}>
                                                    <FontAwesomeIcon icon={faLaptop} className="me-2" style={{ fontSize: '1rem' }} />
                                                    <span className="fw-bold">{hasOfflineMode ? '온라인 가능' : '온라인'}</span>
                                                </span>
                                            )}
                                        </div>

                                        {priceNote && <p className="text-muted small mb-4">*{priceNote}</p>}

                                        {/* <hr className="my-4" />

                                        <button
                                            className="btn btn-primary w-100 py-2 fw-bold shadow-sm"
                                            onClick={clickInquiryBtn}
                                            style={{ borderRadius: '8px', fontSize: '0.95rem' }}
                                        >
                                            전문가에게 문의하기
                                        </button> */}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {showModal && (
                            <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content border-0 shadow" style={{ borderRadius: '15px' }}>
                                        <div className="modal-header border-0 p-4">
                                            <h5 className="fw-bold mb-0">전문가 문의하기</h5>
                                            <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                        </div>
                                        <div className="modal-body p-4 pt-0">
                                            <div className="mb-3">
                                                <label className="form-label small fw-bold">문의 내용</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="8"
                                                    placeholder="궁금하신 내용을 자세히 적어주세요."
                                                    onChange={(e) => setInquiryData({ ...inquiryData, content: e.target.value })}
                                                ></textarea>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label small fw-bold">답변 받을 연락처 (이메일/모바일/메신져)</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="연락처를 남겨주세요."
                                                    onChange={(e) => setInquiryData({ ...inquiryData, contact: e.target.value })}
                                                />
                                            </div>
                                            <div className="form-check small text-muted mb-4">
                                                <input className="form-check-input" type="checkbox" id="agreeCheck" />
                                                <label className="form-check-label" htmlFor="agreeCheck">
                                                    개인정보 제공 및 관리자 모니터링에 동의합니다.
                                                </label>
                                            </div>
                                            <button className="btn btn-primary w-100 py-2 fw-bold" onClick={sendInquiry}>
                                                문의 보내기
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <ModalMessage
                show={showAuthGuideModal}
                closeBtn={closeAuthGuideModal}
                submitBtn={submitAuthGuideModal}
                title={authGuideTitle}
                msg={authGuideMessage}
            />
            {isLoading && (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(128, 128, 128, 0.22)',
                        zIndex: 1050
                    }}
                >
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
        </div>
    );
}